import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthProvider, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AuthLayout } from "@/components/atomic-crm/login/AuthLayout";
import {
  authSubmitButtonClassName,
  authTextClassName,
  ssoButtonClassName,
} from "@/components/atomic-crm/login/authStyles";

/**
 * Authorization UI for OAuth Consent Page
 *
 * When third-party apps initiate OAuth, users will be redirected to this page
 * to approve or deny the authorization request.
 *
 * Anonymous users will be redirected to the login page first.
 *
 * Inspired from https://supabase.com/docs/guides/auth/oauth-server/getting-started?queryGroups=oauth-setup&oauth-setup=dashboard#example-authorization-ui
 */
export function OAuthConsentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authorizationId = searchParams.get("authorization_id");
  const authProvider = useAuthProvider();
  const translate = useTranslate();

  const [authDetails, setAuthDetails] =
    useState<OAuthAuthorizationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [approved, setApproved] = useState(false);

  useEffect(() => {
    async function loadAuthDetails() {
      if (!authorizationId) {
        setError("Missing authorization_id");
        setLoading(false);
        return;
      }
      if (!authProvider) {
        setError("Auth provider not available");
        setLoading(false);
        return;
      }

      // Check if user is authenticated
      try {
        await authProvider.checkAuth({});
      } catch {
        navigate(
          `/login?redirect=/oauth/consent?authorization_id=${authorizationId}`,
        );
        return;
      }

      // Get authorization details using the authorization_id
      const { data, error } =
        await authProvider.getAuthorizationDetails(authorizationId);

      if (error) {
        setError(error.message);
      } else {
        setAuthDetails(data as OAuthAuthorizationDetails);
      }

      setLoading(false);
    }

    loadAuthDetails();
  }, [authProvider, authorizationId, navigate]);

  async function handleApprove() {
    if (!authorizationId || !authProvider) return;

    setSubmitting(true);
    const { data, error } =
      await authProvider.approveAuthorization(authorizationId);

    if (error) {
      setError(error.message);
      setSubmitting(false);
    } else {
      // Show success message and redirect to client app
      setApproved(true);
      window.location.href = data.redirect_url;
    }
  }

  async function handleDeny() {
    if (!authorizationId || !authProvider) return;

    setSubmitting(true);
    const { data, error } =
      await authProvider.denyAuthorization(authorizationId);
    if (error) {
      setError(error.message);
      setSubmitting(false);
    } else {
      // Redirect to client app with error
      window.location.href = data.redirect_url;
    }
  }

  const authorizeTitle = translate("ra-supabase.oauth.authorize", {
    _: "Authorize Application",
  });

  if (loading) {
    return (
      <AuthLayout title={authorizeTitle}>
        <p className={cn("text-center text-sm", authTextClassName)}>
          {translate("ra.message.loading", { _: "Loading..." })}
        </p>
      </AuthLayout>
    );
  }

  if (error) {
    return (
      <AuthLayout title={translate("ra.message.error", { _: "Error" })}>
        <p className="text-center text-sm text-red-200">{error}</p>
      </AuthLayout>
    );
  }

  if (!authDetails) {
    return (
      <AuthLayout title={authorizeTitle}>
        <p className={cn("text-center text-sm", authTextClassName)}>
          {translate("ra-supabase.oauth.no_request", {
            _: "No authorization request found",
          })}
        </p>
      </AuthLayout>
    );
  }

  if (approved) {
    return (
      <AuthLayout
        title={translate("ra-supabase.oauth.approved", {
          _: "Authorization Approved",
        })}
      >
        <p className={cn("text-center text-sm", authTextClassName)}>
          {translate("ra-supabase.oauth.close_tab", {
            _: "You can now close this tab.",
          })}
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={authorizeTitle}
      subtitle={translate("ra-supabase.oauth.authorize_details", {
        _: "This application wants to access your account",
      })}
    >
      {/* A nested <Card> would paint an opaque light panel inside AuthLayout's
          glass card, so the client details get their own glass surface. */}
      <div className="rounded-lg border border-white/20 bg-white/5 p-4">
        <p className="font-semibold text-white">{authDetails.client.name}</p>
        <p className="text-sm break-all text-gray-300">
          {authDetails.redirect_uri}
        </p>
        {authDetails.scope && authDetails.scope.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-300 mb-2">
              {translate("ra-supabase.oauth.permissions", {
                _: "Requested permissions",
              })}
            </p>
            <ul className="list-disc list-inside space-y-1 text-white">
              {authDetails.scope.split(" ").map((scopeItem) => (
                <li key={scopeItem} className="text-sm">
                  {scopeItem}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="mt-6 flex gap-2">
        <Button
          variant="outline"
          onClick={handleDeny}
          disabled={submitting}
          className={cn(ssoButtonClassName, "flex-1")}
        >
          {translate("ra.action.cancel", { _: "Deny" })}
        </Button>
        <Button
          onClick={handleApprove}
          disabled={submitting}
          className={cn(authSubmitButtonClassName, "flex-1")}
        >
          {translate("ra.action.confirm", { _: "Approve" })}
        </Button>
      </div>
    </AuthLayout>
  );
}

OAuthConsentPage.path = "/oauth/consent";

/**
 * copied from @supabase/auth-js/src/lib/types.ts
 * to avoid adding a hard import to a Supabase package
 * because this page can also be used with FakeRest
 */
type OAuthAuthorizationDetails = {
  /** The authorization ID */
  authorization_id: string;
  /** Redirect URL - present if user already consented (can be used to trigger immediate redirect) */
  redirect_uri?: string;
  /** User object associated with the authorization */
  /** OAuth client requesting authorization */
  client: {
    /** Unique identifier for the OAuth client (UUID) */
    id: string;
    /** Human-readable name of the OAuth client */
    name: string;
    /** URI of the OAuth client's website */
    uri: string;
    /** URI of the OAuth client's logo */
    logo_uri: string;
  };
  user: {
    /** User ID (UUID) */
    id: string;
    /** User email */
    email: string;
  };
  /** Space-separated list of requested scopes */
  scope: string;
};
